import React, { Component } from "react";
import { observer } from "mobx-react";

import { defined } from "@formant/core/common/defined";
import { IStream } from "@formant/core/model/IStream";

import { routerStore, streamStore } from "../../../stores";
import { localSettingObservable } from "../../../utils/localSettingObservable";
import {
    DataTable,
    defaultSimpleSortState,
    ISimpleSortState,
} from "../../common/DataTable";
import { IDataTableColumn } from "../../common/DataTable/IDataTableColumn";
import { Help } from "../../common/Help";
import { Scrollable } from "../../common/Scrollable";
import {
    applySimpleFilter,
    emptySimpleFilterState,
    ISimpleFilterState,
    SimpleFilter,
} from "../../common/SimpleFilter";
import { StandardButton } from "../../common/StandardButton";
import { StatsHeader } from "../../common/StatsHeader";
import { streamTypeIcons } from "../../common/StreamIcon";
import { RowDate } from "../RowDate";
import { RowTitle } from "../RowTitle";
import { SettingsPage } from "../SettingsPage";
import { Actions } from "./Actions";

@observer
export class Streams extends Component {
    @localSettingObservable("settings-streams-filter")
    private filter: ISimpleFilterState = emptySimpleFilterState;

    @localSettingObservable("settings-streams-sort")
    private sort: ISimpleSortState = defaultSimpleSortState;

    public render() {
        const { routeTo } = routerStore;
        const columns: IDataTableColumn<IStream>[] = [
            {
                header: "Name",
                row: _ => (
                    <RowTitle
                        title={_.streamName}
                        icon={streamTypeIcons[_.streamType]}
                    />
                ),
                sortBy: _ => _.streamName,
            },
            {
                header: "Type",
                row: _ => _.streamType,
                sortBy: _ => _.streamType,
            },
            {
                header: "Telemetry filter",
                row: _ => (_.isTelemetryFilter ? "enabled" : "disabled"),
                sortBy: _ => Boolean(_.isTelemetryFilter).toString(),
            },
            {
                header: "Event filter",
                row: _ => (_.isEventFilter ? "enabled" : "disabled"),
                sortBy: _ => Boolean(_.isEventFilter).toString(),
            },
            {
                header: "Active",
                row: _ => (_.active ? "active" : "inactive"),
                sortBy: _ => Boolean(_.active).toString(),
            },
            {
                header: "Created",
                row: _ => <RowDate date={_.createdAt} />,
                sortBy: _ => _.createdAt,
                hideBelow: "md",
            },
            {
                header: "Updated",
                row: _ => <RowDate date={_.updatedAt} />,
                sortBy: _ => _.updatedAt,
                hideBelow: "md",
            },
            {
                header: "",
                row: _ => <Actions stream={_} />,
            },
        ];

        const streams = streamStore.list();
        const { filter, onChangeFilter, sort, onChangeSort } = this;

        const dimensions = [
            {
                dimensionKey: "streamType",
                dimensionName: "Type",
                getValue: (_: IStream) => _.streamType,
            },
            {
                dimensionKey: "isTelemetryFilter",
                dimensionName: "Telemetry filter",
                getValue: (_: IStream) =>
                    _.isTelemetryFilter ? "enabled" : "disabled",
            },
            {
                dimensionKey: "isEventFilter",
                dimensionName: "Event filter",
                getValue: (_: IStream) =>
                    _.isEventFilter ? "enabled" : "disabled",
            },
            {
                dimensionKey: "active",
                dimensionName: "Active",
                getValue: (_: IStream) => (_.active ? "active" : "inactive"),
            },
        ];

        const filtered = applySimpleFilter(
            streams || [],
            dimensions,
            _ => _.streamName,
            filter,
        );

        return (
            <SettingsPage
                title="Streams"
                extra={
                    <Help
                        text="A stream is a logical channel of telemetry"
                        // to="https://docs.formant.io/docs/how-telemetry-streams-work" // TODO
                    />
                }
                actions={
                    <StandardButton to={routeTo.createStream()}>
                        Add stream
                    </StandardButton>
                }
            >
                <StatsHeader
                    icon="stream"
                    loading={!streams}
                    totalQuantity={streams?.length || 0}
                    totalLabel="Streams"
                />
                <SimpleFilter
                    dimensions={dimensions}
                    rows={streams || []}
                    filter={filter}
                    onChangeFilter={onChangeFilter}
                />
                <Scrollable>
                    <DataTable
                        rows={filtered || []}
                        columns={columns}
                        loading={streams === undefined}
                        noResultsText="No streams found"
                        onRowClicked={this.onEdit}
                        sort={sort}
                        onChangeSort={onChangeSort}
                        rowTestId={_ => `streams-settings-${_.streamName}`}
                    />
                </Scrollable>
            </SettingsPage>
        );
    }

    private onChangeFilter = (filter: ISimpleFilterState) => {
        this.filter = filter;
    };

    private onChangeSort = (sort: ISimpleSortState) => {
        this.sort = sort;
    };

    private onEdit = async (stream: IStream) => {
        const { navigate, routeTo } = routerStore;
        await navigate(
            routeTo.editStream({ id: defined(stream.id) }, { tab: "Settings" }),
        );
    };
}
